import React, { useState, useEffect } from 'react';
import { Users, Plus } from 'lucide-react';
import { useAuth } from '../../../App'; // Import from App.js
import CreateSpaceButton from './CreateSpaceButton';
import SharingSpaceItem from './SharingSpaceItem';
import InvitationsList from './InvitationsList';
import PremiumUpgradeBanner from './PremiumUpgradeBanner';
import CreateSpaceForm from './CreateSpaceForm';
import SharingSpaceDetails from './SharingSpaceDetails';
import { useTranslation } from '../../../contexts/LanguageContext';

const SharingSpacesCard = ({
  isPremium,
  sharingSpaces,
  setSharingSpaces,
  activeTab,
  setActiveTab,
  isLoading,
  activeSpace,
  setActiveSpace,
  onCreateSpace,
  onUpgrade,
  showCreateForm,
  pendingInvitations,
  setPendingInvitations,
  fetchUserData,
  fetchPendingInvitations,
  hasPendingInvites,
  onDeleteSpace,
  onRemoveMember,
  onRemoveSpaceSubscription,
  onRemovePersonalSubscription,
  onUpdateBalance,
  onInviteNewMembers,
  onAddSubscriptions,
  onCancelInvitation,
  friends = [],
  userSubscriptions = [],
  formProps
}) => {
  const { t } = useTranslation();
  const { user, httpClient } = useAuth(); // Get user and httpClient from auth context

  const handleSpaceClick = (space) => {
    setActiveSpace(space);
    if (formProps && formProps.onCancel) {
      formProps.onCancel();
    }
  };

  const handleCreateSpace = () => {
    setActiveSpace(null);
    onCreateSpace();
  };

  const handleInvitationResponse = async (invitationId, spaceId, action) => {
    try {
      if (!httpClient || !user) return;

      const response = await httpClient.request(
        `/api/sharing-space/invitation/${action}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invitation_id: invitationId,
            sharing_space_id: spaceId
          })
        }
      );
      
      if (!response.ok) {
        throw new Error(t(`sharesubs.errors.failedTo${action === 'accept' ? 'Accept' : 'Reject'}Invitation`));
      }
      
      setPendingInvitations(prev => 
        prev.filter(invitation => invitation._id !== invitationId)
      );
      
      if (action === 'accept') {
        await fetchUserData();
      }
      
      await fetchPendingInvitations();
      
    } catch (error) {
      console.error(`Error handling invitation:`, error);
      alert(`${t(`sharesubs.errors.failedTo${action === 'accept' ? 'Accept' : 'Reject'}Invitation`)}: ${error.message}`);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          {t('sharesubs.title')}
        </h2>
        {!isPremium && (
          <div className="text-sm text-gray-600">
            {sharingSpaces.length}/3 {t('sharesubs.status.spacesUsed')}
          </div>
        )}
      </div>

      <div className="flex border-b mb-6">
        <button
          className={`py-2 px-4 font-medium ${activeTab === 'spaces' ? 'text-[#253985] border-b-2 border-[#253985]' : 'text-gray-500'}`}
          onClick={() => setActiveTab('spaces')}
        >
          {t('sharesubs.tabs.spaces')}
        </button>
        <button
          className={`py-2 px-4 font-medium ${activeTab === 'invitations' ? 'text-[#253985] border-b-2 border-[#253985]' : 'text-gray-500'}`}
          onClick={() => setActiveTab('invitations')}
        >
          {t('sharesubs.tabs.invitations')}
          {hasPendingInvites && (
            <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
              New
            </span>
          )}
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#253985] mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {t('sharesubs.status.loadingSpaces')}...
          </p>
        </div>
      ) : activeTab === 'spaces' ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-6">
            <CreateSpaceButton 
              isPremium={isPremium}
              spacesCount={sharingSpaces.length}
              maxFreeSpaces={3}
              onClick={handleCreateSpace}
              onUpgrade={onUpgrade}
            />
            {sharingSpaces.map(space => (
              <SharingSpaceItem 
                key={space.id} 
                space={space}
                currentUserId={user?.userId}
                isActive={!showCreateForm && activeSpace && activeSpace.id === space.id}
                onClick={() => handleSpaceClick(space)}
              />
            ))}
          </div>
          
          {showCreateForm && 
            <CreateSpaceForm 
              {...formProps}
            />
          }
          
          {sharingSpaces.length === 0 && !showCreateForm && (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <Users className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-600 mb-2">
                {t('sharesubs.emptyState.title')}
              </h3>
              <p className="text-gray-500 mb-6">
                {t('sharesubs.emptyState.description')}
              </p>
              <button
                onClick={handleCreateSpace}
                className="px-6 py-3 bg-[#253985] text-white rounded-lg hover:bg-[#1a2c6d] flex items-center mx-auto"
              >
                <Plus className="h-5 w-5 mr-2" />
                {t('sharesubs.actions.createSpace')}
              </button>
            </div>
          )}
          
          {activeSpace && !showCreateForm && (
            <SharingSpaceDetails 
              space={activeSpace}
              setSharingSpaces={setSharingSpaces}
              onDelete={onDeleteSpace}
              onRemoveMember={onRemoveMember}
              onRemoveSpaceSubscription={onRemoveSpaceSubscription}
              onRemovePersonalSubscription={onRemovePersonalSubscription}
              onUpdateBalance={onUpdateBalance}
              onInviteNewMembers={onInviteNewMembers}  
              onAddSubscriptions={onAddSubscriptions} 
              onCancelInvitation={onCancelInvitation}
              friends={friends}
              userSubscriptions={userSubscriptions}
              sharingSpaces={sharingSpaces}
              setActiveSpace={setActiveSpace}
              activeSpace={activeSpace}    
            />
          )}
        </div>
      ) : (
        <InvitationsList 
          invitations={pendingInvitations}
          onInvitationResponse={handleInvitationResponse}
          isLoading={isLoading}
        />
      )}

      {!isPremium && !showCreateForm && (
        <PremiumUpgradeBanner 
          isPremium={isPremium}
          spacesCount={sharingSpaces.length}
          maxFreeSpaces={3}
          onUpgrade={onUpgrade}
        />
      )}
    </div>
  );
};

export default SharingSpacesCard;